import React, { useState, useEffect, useRef } from 'react';
import './index.css';
import Header from './components/Header';
import CalendarPanel from './components/CalendarPanel';
import MealPanel from './components/MealPanel';
import GroceryPanel from './components/GroceryPanel';
import QuickAddPanel from './components/QuickAddPanel';
import PetTracker from './components/PetTracker';
import Reports from './components/Reports';
import Chores from './components/Chores';
import DadStatus from './components/DadStatus';
import MobileLayout from './components/MobileLayout';
import LoginScreen from './components/LoginScreen';
import ManageScreen from './components/ManageScreen';
import { getToken, clearToken } from './api';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const DESKTOP_VIEWS = [
  { id: 'home',    label: 'Home'    },
  { id: 'chores',  label: 'Chores'  },
  { id: 'reports', label: 'Reports' },
];

export default function App() {
  const [authed, setAuthed]     = useState(!!getToken());
  const [managing, setManaging] = useState(false);
  const [view, setView]         = useState('home');
  const [theme, setTheme]       = useState(() => localStorage.getItem('theme') || 'dark');
  const isMobile = useIsMobile();
  const groceryRef = useRef();

  useEffect(() => {
    document.documentElement.className = theme === 'light' ? 'light' : '';
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const handler = () => setAuthed(false);
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  return (
    <div style={styles.root}>
      <Header
        onLogout={() => { clearToken(); setAuthed(false); }}
        onManage={() => setManaging(true)}
        isMobile={isMobile}
        view={view}
        onViewChange={setView}
        views={DESKTOP_VIEWS}
        theme={theme}
        onThemeToggle={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
      />
      {isMobile ? (
        <MobileLayout groceryRef={groceryRef} />
      ) : view === 'chores' ? (
        <div style={styles.fullPage}>
          <Chores />
        </div>
      ) : view === 'reports' ? (
        <div style={styles.fullPage}>
          <Reports />
        </div>
      ) : (
        <div style={styles.homeWrap}>
          <div style={styles.statusBar}>
            <DadStatus />
          </div>
          <div style={styles.body}>
            {/* Left: Calendar + Meals */}
            <div style={styles.leftCol}>
              <div style={styles.card}><CalendarPanel /></div>
              <div style={styles.card}><MealPanel /></div>
            </div>
            {/* Center: Quick Add + Grocery */}
            <div style={styles.centerCol}>
              <div style={styles.cardFlat}>
                <QuickAddPanel onItemAdded={() => groceryRef.current?.reload()} />
              </div>
              <div style={{...styles.card, flex: 1}}>
                <GroceryPanel ref={groceryRef} />
              </div>
            </div>
            {/* Right: Pets */}
            <div style={styles.rightCol}>
              <div style={{...styles.card, flex: 1}}><PetTracker /></div>
            </div>
          </div>
        </div>
      )}
      {managing && <ManageScreen onClose={() => setManaging(false)} />}
    </div>
  );
}

const styles = {
  root: {
    display: 'flex', flexDirection: 'column',
    height: '100vh', width: '100vw',
    overflow: 'hidden', background: 'var(--bg)',
  },
  homeWrap: {
    display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden',
  },
  statusBar: {
    padding: '8px 12px 0', flexShrink: 0,
  },
  body: {
    display: 'flex', flex: 1, gap: 12, padding: 12, overflow: 'hidden',
  },
  fullPage: {
    flex: 1, padding: '16px 28px', overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  },
  leftCol: {
    display: 'flex', flexDirection: 'column', gap: 12, width: 280, flexShrink: 0,
  },
  centerCol: {
    display: 'flex', flexDirection: 'column', gap: 12, flex: 1, overflow: 'hidden',
  },
  rightCol: {
    display: 'flex', flexDirection: 'column', width: 300, flexShrink: 0,
  },
  card: {
    background: 'var(--surface)', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)', padding: '14px 16px',
    overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1,
  },
  cardFlat: {
    background: 'var(--surface)', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)', padding: '14px 16px', flexShrink: 0,
  },
};
